import { Code, Text } from "@chakra-ui/react";
import { RiErrorWarningLine } from "@remixicon/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router";

import {
  DebugProblemDetails,
  type DebugProblemCursor,
} from "@/app/components/DebugProblemDetails";
import { useDebugProblemsData } from "@/app/data/useDebugProblemsData";
import {
  formatProblemCode,
  getProblemHeadline,
} from "@/app/utils/debugProblems";
import { CompanyAutocompleteSelect } from "@/common/components/Autocomplete/CompanyAutocompleteSelect";
import { FeatureAutocompleteSelect } from "@/common/components/Autocomplete/FeatureAutocompleteSelect";
import { UserAutocompleteSelect } from "@/common/components/Autocomplete/UserAutocompleteSelect";
import { DataTable } from "@/common/components/DataTable/DataTable";
import EmptyState from "@/common/components/EmptyState";
import { FillScrollWrapper } from "@/common/components/FillScrollWrapper";
import {
  IcoFeature,
  IcoOrganization,
  IcoUser,
} from "@/common/components/Icons";
import SidebarLayout from "@/common/components/SidebarLayout";
import TimestampCell from "@/common/components/TimestampCell";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSidebar } from "@/common/hooks/useSidebar";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import { useFlag } from "@reflag/react-sdk";
import type { DebugProblem } from "@reflag/shared/eventAPI";
import type { ColumnSort } from "@reflag/shared/types/columns";

type DebugProblemSortBy = "title" | "lastSeen" | "count";

function clearProblemSelection(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of [
    "problemCode",
    "problemField",
    "problemOperator",
    "eventId",
    "timestamp",
  ]) {
    next.delete(key);
  }
  return next;
}

const defaultSorting: ColumnSort<DebugProblemSortBy>[] = [
  { id: "lastSeen", desc: true },
];

function sortProblems(
  problems: DebugProblem[],
  sorting: ColumnSort<DebugProblemSortBy>[],
): DebugProblem[] {
  const { id, desc } = sorting[0] ?? defaultSorting[0];

  return [...problems].sort((left, right) => {
    let comparison: number;
    switch (id) {
      case "count":
        comparison = left.count - right.count;
        break;
      case "lastSeen":
        comparison =
          new Date(left.lastSeen).valueOf() -
          new Date(right.lastSeen).valueOf();
        break;
      case "title":
        comparison = getProblemHeadline(left).localeCompare(
          getProblemHeadline(right),
        );
        break;
    }

    if (comparison === 0) {
      comparison = left.id.localeCompare(right.id);
    }
    return desc ? -comparison : comparison;
  });
}

const columnHelper = createSortableColumnHelper<
  DebugProblem,
  DebugProblemSortBy
>();
const problemColumns = [
  columnHelper.accessor(getProblemHeadline, {
    id: "title",
    header: "Title",
    cell: ({ row }) => (
      <Text fontSize="sm" fontWeight="medium" py={0.5}>
        {formatProblemCode(row.original.code)}:{" "}
        <Code wordBreak="break-all">{row.original.field}</Code>
        {row.original.operator && ` ${row.original.operator}`}
      </Text>
    ),
  }),
  columnHelper.accessor("lastSeen", {
    header: "Last seen",
    size: 180,
    sortDescFirst: true,
    cell: (cell) => (
      <TimestampCell value={cell.getValue()} autoUpdate leftAlign />
    ),
  }),
  columnHelper.accessor("count", {
    header: "Count",
    size: 120,
    sortDescFirst: true,
    cell: (cell) => (
      <Text fontSize="sm">{cell.getValue().toLocaleString()}</Text>
    ),
  }),
];

export default function DebuggerProblems() {
  const { isEnabled } = useFlag("event-log-problem-filter");
  return isEnabled ? (
    <DebuggerProblemsTable />
  ) : (
    <Navigate to="../recent" replace />
  );
}

function DebuggerProblemsTable() {
  const [params, setParams] = useSearchParams();
  const companyId = params.get("company") ?? undefined;
  const userId = params.get("user") ?? undefined;
  const featureId = params.get("feature") ?? undefined;
  const code = params.get("problemCode");
  const field = params.get("problemField");
  const operator = params.get("problemOperator") || undefined;
  const activeProblem = code && field ? { code, field, operator } : null;
  const eventId = params.get("eventId");
  const timestamp = params.get("timestamp");
  const occurrence =
    eventId !== null || timestamp !== null
      ? { eventId: eventId ?? "", timestamp: timestamp ?? "" }
      : undefined;
  const { appId, envId } = useEnvironment();
  const { show: showSidebar } = useSidebar("debugger");
  const scope = JSON.stringify([
    appId,
    envId,
    companyId,
    userId,
    featureId,
    code,
    field,
    operator,
    occurrence,
  ]);

  const openedScope = useRef<string>();
  useEffect(() => {
    if (!code || !field) {
      openedScope.current = undefined;
    } else if (openedScope.current !== scope) {
      openedScope.current = scope;
      showSidebar();
    }
  }, [code, field, scope, showSidebar]);

  const setOccurrence = useCallback(
    (cursor: DebugProblemCursor | undefined, replace = true) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (cursor) {
            next.set("eventId", cursor.eventId);
            next.set("timestamp", cursor.timestamp);
          } else {
            next.delete("eventId");
            next.delete("timestamp");
          }
          return next;
        },
        { replace },
      );
    },
    [setParams],
  );
  const setFilter = (key: string, value: string | null | undefined) => {
    setParams(
      (previous) => {
        const next = clearProblemSelection(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };
  const [sorting, setSorting] =
    useState<ColumnSort<DebugProblemSortBy>[]>(defaultSorting);

  const {
    data: unsortedProblems = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useDebugProblemsData({ companyId, userId, featureId });
  const problems = useMemo(
    () => sortProblems(unsortedProblems, sorting),
    [sorting, unsortedProblems],
  );

  return (
    <SidebarLayout
      flexGrow={1}
      minH={0}
      overflow="hidden"
      sidebarKey="debugger"
      sidebarProps={{ display: "flex", flexDirection: "column", minH: 0 }}
      templateColumns="repeat(3, minmax(0, 1fr)) clamp(360px, 34%, 520px)"
      sidebarContent={
        activeProblem && (
          <FillScrollWrapper key={scope} enabled>
            <DebugProblemDetails
              problem={activeProblem}
              filters={{ companyId, userId, featureId }}
              occurrence={occurrence}
              onOccurrenceChange={setOccurrence}
              onClose={() =>
                setParams(clearProblemSelection, { replace: true })
              }
            />
          </FillScrollWrapper>
        )
      }
    >
      <DataTable
        _toolbarLeft={{ wrap: "wrap", alignSelf: "flex-start" }}
        columns={problemColumns}
        data={problems}
        emptyState={
          <EmptyState
            description="Evaluation errors from access checks in this environment will appear here."
            error="Problems could not be loaded."
            h="100%"
            icon={<RiErrorWarningLine size={48} />}
            isError={isError}
            isLoading={isLoading}
            title="No problems in the last 24 hours"
            onRetry={() => void refetch()}
          />
        }
        isFetching={isFetching}
        highlightRow={({ original }) =>
          original.code === activeProblem?.code &&
          original.field === activeProblem?.field &&
          original.operator === activeProblem?.operator
        }
        sorting={sorting}
        tableId="debug-problems"
        toolbarLeftActions={() => (
          <>
            <Text alignSelf="center" color="dimmed" fontSize="sm">
              Problems detected in the last 24 hours
            </Text>
            <CompanyAutocompleteSelect
              leftIcon={<IcoOrganization color="dimmed" size="sm" />}
              maxW="lg"
              placeholder="Any company"
              value={companyId}
              canClear
              valueAsId
              onChange={(value) => setFilter("company", value)}
            />
            <UserAutocompleteSelect
              leftIcon={<IcoUser color="dimmed" size="sm" />}
              maxW="lg"
              placeholder="Any user"
              value={userId}
              canClear
              valueAsId
              onChange={(value) => setFilter("user", value)}
            />
            <FeatureAutocompleteSelect
              leftIcon={<IcoFeature color="dimmed" size="sm" />}
              maxW="lg"
              placeholder="Any flag"
              value={featureId}
              canClear
              valueAsId
              onChange={(value) => setFilter("feature", value)}
            />
          </>
        )}
        canSort
        variant="clickable"
        onRowClick={(row) => {
          showSidebar();
          setParams((previous) => {
            const next = clearProblemSelection(previous);
            next.set("problemCode", row.original.code);
            next.set("problemField", row.original.field);
            if (row.original.operator) {
              next.set("problemOperator", row.original.operator);
            }
            return next;
          });
        }}
        onSortingChange={setSorting}
      />
    </SidebarLayout>
  );
}
